/**
 * Shared attachment UI for the EXISTING staff-messaging system.
 * Upload: POST /api/messages/attachments (one file per request, staged).
 * Download: GET /api/messages/attachments/{id} — server-side authorised,
 * fetched as a blob with the auth header (no public URLs).
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Paperclip, X, FileText, Download, Camera, Image as ImageIcon, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'heif'];
const ALLOWED_EXTS = new Set([...IMAGE_EXTS, 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv', 'txt']);
export const ATTACH_ACCEPT = [...ALLOWED_EXTS].map((e) => `.${e}`).join(',');
const MAX_BYTES = 10 * 1024 * 1024;
export const MAX_ATTACHMENTS = 5;

export const fmtSize = (n) => {
  if (!n && n !== 0) return '';
  if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
};

const extOf = (name = '') => (name.includes('.') ? name.split('.').pop().toLowerCase() : '');
const isImageFile = (f) => (f.type || '').startsWith('image/') || IMAGE_EXTS.includes(extOf(f.name));

export const filterNewFiles = (existing, incoming) => {
  const out = [];
  for (const f of incoming) {
    if (!ALLOWED_EXTS.has(extOf(f.name))) { toast.error(`${f.name}: file type not allowed`); continue; }
    if (f.size > MAX_BYTES) { toast.error(`${f.name}: too large (max 10 MB)`); continue; }
    if (existing.length + out.length >= MAX_ATTACHMENTS) { toast.error(`Max ${MAX_ATTACHMENTS} attachments per message`); break; }
    out.push(f);
  }
  return out;
};

export const uploadAttachments = async (files) => {
  const ids = [];
  for (const f of files) {
    const fd = new FormData();
    fd.append('file', f);
    const r = await axios.post(`${API}/messages/attachments`, fd);
    ids.push(r.data.id);
  }
  return ids;
};

// Drag & drop over the composer (desktop)
export const useFileDrop = (onFiles) => {
  const [dragOver, setDragOver] = useState(false);
  const counter = useRef(0);
  const dropProps = {
    onDragEnter: (e) => { e.preventDefault(); counter.current += 1; setDragOver(true); },
    onDragOver: (e) => { e.preventDefault(); },
    onDragLeave: (e) => { e.preventDefault(); counter.current -= 1; if (counter.current <= 0) { counter.current = 0; setDragOver(false); } },
    onDrop: (e) => {
      e.preventDefault(); counter.current = 0; setDragOver(false);
      const fs = Array.from(e.dataTransfer?.files || []);
      if (fs.length) onFiles(fs);
    },
  };
  return { dragOver, dropProps };
};

// Paperclip menu: Upload File / Choose Photo / Take Photo (native pickers)
export const AttachMenu = ({ onFiles, disabled, testPrefix = 'msg' }) => {
  const fileRef = useRef(null);
  const photoRef = useRef(null);
  const cameraRef = useRef(null);
  const [open, setOpen] = useState(false);
  const pick = (e) => {
    const fs = Array.from(e.target.files || []);
    if (fs.length) onFiles(fs);
    e.target.value = '';
    setOpen(false);
  };
  const Item = ({ icon: Icon, label, onClick, testId }) => (
    <button type="button" onClick={onClick} data-testid={testId}
      className="w-full flex items-center gap-2 px-2.5 py-2 text-xs rounded-sm hover:bg-accent text-foreground text-left">
      <Icon className="w-3.5 h-3.5 text-muted-foreground" /> {label}
    </button>
  );
  return (
    <>
      <input ref={fileRef} type="file" multiple accept={ATTACH_ACCEPT} className="hidden" onChange={pick} data-testid={`${testPrefix}-attach-input-file`} />
      <input ref={photoRef} type="file" multiple accept="image/*" className="hidden" onChange={pick} data-testid={`${testPrefix}-attach-input-photo`} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`${testPrefix}-attach-input-camera`} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="icon" disabled={disabled}
            className="rounded-sm h-10 w-10 shrink-0 border-border" title="Attach files"
            data-testid={`${testPrefix}-attach-btn`}>
            <Paperclip className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" side="top" className="w-44 p-1 rounded-sm">
          <Item icon={Upload} label="Upload File" onClick={() => fileRef.current?.click()} testId={`${testPrefix}-attach-upload-file`} />
          <Item icon={ImageIcon} label="Choose Photo" onClick={() => photoRef.current?.click()} testId={`${testPrefix}-attach-choose-photo`} />
          <Item icon={Camera} label="Take Photo" onClick={() => cameraRef.current?.click()} testId={`${testPrefix}-attach-take-photo`} />
        </PopoverContent>
      </Popover>
    </>
  );
};

const PendingChip = ({ file, onRemove, testId }) => {
  const [thumb, setThumb] = useState(null);
  useEffect(() => {
    if (!isImageFile(file)) return undefined;
    const url = URL.createObjectURL(file);
    setThumb(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return (
    <div className="flex items-center gap-2 bg-muted/50 border border-border rounded-sm pl-1.5 pr-1 py-1 max-w-[220px]" data-testid={testId}>
      {thumb ? (
        <img src={thumb} alt={file.name} className="w-8 h-8 object-cover rounded-sm shrink-0" />
      ) : (
        <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
      )}
      <div className="min-w-0">
        <p className="text-[11px] text-foreground truncate">{file.name}</p>
        <p className="text-[10px] text-muted-foreground">{fmtSize(file.size)}</p>
      </div>
      <button type="button" onClick={onRemove} className="p-1 hover:text-red-500 text-muted-foreground shrink-0" data-testid={`${testId}-remove`} title="Remove">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

// Preview of selected attachments inside the composer, removable before send
export const PendingAttachments = ({ files, onRemove, testPrefix = 'msg' }) => {
  if (!files.length) return null;
  return (
    <div className="flex flex-wrap gap-2 px-3 pt-2" data-testid={`${testPrefix}-pending-attachments`}>
      {files.map((f, i) => (
        <PendingChip key={`${f.name}-${i}`} file={f} onRemove={() => onRemove(i)} testId={`${testPrefix}-pending-att-${i}`} />
      ))}
    </div>
  );
};

// Authenticated image: fetched as blob so the auth header applies
const AuthImage = ({ att, className, onClick, testId }) => {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url;
    let active = true;
    axios.get(`${API}/messages/attachments/${att.id}`, { responseType: 'blob' })
      .then((r) => { url = URL.createObjectURL(r.data); if (active) setSrc(url); })
      .catch(() => active && setFailed(true));
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [att.id]);
  if (failed) return <p className="text-[11px] text-muted-foreground italic">Image unavailable</p>;
  if (!src) return <div className={`${className} bg-muted/50 animate-pulse`} />;
  return <img src={src} alt={att.filename} className={className} onClick={onClick} data-testid={testId} loading="lazy" />;
};

export const downloadAttachment = async (att) => {
  try {
    const r = await axios.get(`${API}/messages/attachments/${att.id}`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = att.filename || 'file';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch {
    toast.error('You do not have access to this file');
  }
};

// Attachments rendered inside an existing message bubble
export const AttachmentList = ({ attachments }) => {
  const [viewer, setViewer] = useState(null);
  if (!attachments?.length) return null;
  const images = attachments.filter((a) => a.is_image);
  const docs = attachments.filter((a) => !a.is_image);
  return (
    <div className="mt-2 space-y-2">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((a) => (
            <AuthImage key={a.id} att={a} testId={`att-image-${a.id}`}
              className="max-h-40 max-w-full rounded-sm border border-border cursor-pointer object-cover"
              onClick={() => setViewer(a)} />
          ))}
        </div>
      )}
      {docs.map((a) => (
        <button key={a.id} type="button" onClick={() => downloadAttachment(a)} data-testid={`att-doc-${a.id}`}
          className="w-full flex items-center gap-2 bg-background/60 border border-border rounded-sm px-2.5 py-2 hover:bg-accent/50 text-left">
          <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-foreground truncate">{a.filename}</p>
            <p className="text-[10px] text-muted-foreground uppercase">{extOf(a.filename)} · {fmtSize(a.size)}</p>
          </div>
          <Download className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        </button>
      ))}
      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-w-3xl p-2 bg-background" data-testid="attachment-viewer">
          <DialogTitle className="sr-only">{viewer?.filename}</DialogTitle>
          {viewer && (
            <>
              <AuthImage att={viewer} className="max-h-[80vh] w-auto mx-auto rounded-sm" />
              <div className="flex items-center justify-between px-1">
                <p className="text-xs text-muted-foreground truncate">{viewer.filename}</p>
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => downloadAttachment(viewer)} data-testid="attachment-viewer-download">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Download
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
